/**
 * List detail page: title block, toolbar, and the restaurants in the list as a compact table
 * with an optional map beside it.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Check, Pencil, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/data-table";
import { BackLink, PageHeader, StatStrip } from "@/components/shared/page-header";
import { SearchField } from "@/components/shared/search-field";
import { GoogleMapsLoader } from "@/components/shared/google-maps-loader";
import { ViewToggle, type ViewMode } from "@/components/shared/view-toggle";
import { CITIES, type CityKey } from "@/constants/cities";
import { useAuth } from "@/providers/auth.provider";

import { useListById, useUpdateList } from "../hooks/use-lists";
import { getListTypeInfo } from "../constants/list-types";
import { ListTypeSelector } from "../components/list-type-selector";
import { AddRestaurantModal } from "../components/add-restaurant-modal";
import { ShareListButton } from "../components/share-list-button";
import RestaurantMap from "../../map/components/restaurant-map";
import { getRestaurantColumns, restaurantMapColumns } from "../../restaurants/components/restaurant-columns";
import { Restaurant } from "../../restaurants/types/restaurants.types";
import { RestaurantExpandedRow } from "../../restaurants/components/restaurant-details";

const PORTLAND = CITIES.portland;

interface ListDetailsPageProps {
  listId: string;
}

const ListDetailsPage: React.FC<ListDetailsPageProps> = ({ listId }) => {
  const { user } = useAuth();
  const { data: listData, isLoading, error } = useListById(listId);
  const updateList = useUpdateList();

  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);
  const [hoveredRestaurantId, setHoveredRestaurantId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isGoogleApiLoaded, setIsGoogleApiLoaded] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");

  const list = listData?.data;
  const isOwner = Boolean(user && list && user.id === list.userId);

  const restaurants = useMemo<Restaurant[]>(
    () =>
      (list?.listItems ?? []).map((item) => ({
        ...item.restaurant,
        lists: [],
        hearts: item.hearts,
        createdAt: typeof item.createdAt === "string" ? item.createdAt : new Date(item.createdAt).toISOString(),
      })) as Restaurant[],
    [list?.listItems]
  );

  // The name cell opens the row; keep the column array stable by forwarding through a ref to the latest handler.
  const rowClickRef = useRef<(restaurant: Restaurant) => void>(() => {});
  const openFromName = useCallback((restaurant: Restaurant) => rowClickRef.current(restaurant), []);
  const columns = useMemo(
    () => getRestaurantColumns({ listId, showLists: false, onOpen: openFromName }),
    [listId, openFromName]
  );
  const handleGoogleApiLoaded = useCallback(() => setIsGoogleApiLoaded(true), []);

  const table = useReactTable({
    data: restaurants,
    columns: viewMode === "map" ? restaurantMapColumns : columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (r) => r.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
  });

  const mapRestaurants = useMemo(
    () =>
      restaurants.map((r) => ({
        id: r.id,
        placeId: r.placeId,
        name: r.name,
        formattedAddress: r.formattedAddress,
        latitude: r.latitude,
        longitude: r.longitude,
        rating: r.rating,
        priceLevel: r.priceLevel,
        types: r.types,
        photos: r.photos,
      })),
    [restaurants]
  );

  const defaultCenter = useMemo(() => {
    const coords = restaurants
      .map((r) => ({ lat: parseFloat(r.latitude), lng: parseFloat(r.longitude) }))
      .filter((c) => !Number.isNaN(c.lat) && !Number.isNaN(c.lng));
    if (coords.length === 0) return PORTLAND;
    return {
      lat: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
      lng: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
    };
  }, [restaurants]);

  const handleMapPinClick = (restaurant: { id: string }) => {
    setSelectedRestaurantId(restaurant.id);
    document.getElementById(`row-${restaurant.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Clicking a row (or its name) expands its details in place; in map mode it also centers the map.
  const handleRowClick = (restaurant: Restaurant) => {
    const next = selectedRestaurantId === restaurant.id ? null : restaurant.id;
    setSelectedRestaurantId(next);
    if (next && viewMode === "map") setMapCenter({ lat: parseFloat(restaurant.latitude), lng: parseFloat(restaurant.longitude) });
  };
  rowClickRef.current = handleRowClick;

  const startEditTitle = () => {
    if (!list) return;
    setEditedTitle(list.listName);
    setIsEditingTitle(true);
  };

  const saveTitle = async () => {
    if (!list || !editedTitle.trim() || editedTitle.trim() === list.listName) {
      setIsEditingTitle(false);
      return;
    }
    await updateList.mutateAsync({ listId: list.id, data: { listName: editedTitle.trim() } });
    setIsEditingTitle(false);
  };

  const cancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditedTitle("");
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5" aria-busy="true">
        <PageHeader backTo={{ to: "/dashboard/lists", label: "Lists" }} title="Loading" emphasis="list" />
        <DataTable table={table} isLoading />
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader backTo={{ to: "/dashboard/lists", label: "Lists" }} title="List not found" />
        <p className="text-[13.5px] text-muted-foreground">
          This list does not exist or you do not have access to it.
        </p>
      </div>
    );
  }

  const typeInfo = getListTypeInfo(list.listType);
  // The server's `List` type omits `city` even though the column is selected and returned;
  // cast narrowly here rather than widen the shared server type.
  const listCity = list.city;
  const cityName = listCity ? CITIES[listCity as CityKey]?.name ?? listCity : null;
  const stats = [
    { value: restaurants.length, label: restaurants.length === 1 ? "place" : "places" },
    ...(cityName ? [{ value: cityName, label: "city" }] : []),
    ...(list.creatorName ? [{ value: isOwner ? "You" : list.creatorName, label: "created by" }] : []),
    { value: new Date(list.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }), label: "created" },
  ];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-3">
        <BackLink to="/dashboard/lists" label="Lists" />
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <div className="flex min-w-0 flex-col gap-2">
            {isEditingTitle ? (
              <div className="flex items-center gap-1.5">
                <Input
                  id="list-title"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void saveTitle(); }
                    if (e.key === "Escape") cancelEditTitle();
                  }}
                  className="h-10 w-72 font-display text-2xl text-brand"
                  autoFocus
                  disabled={updateList.isPending}
                  aria-label="List name"
                />
                <Button size="icon-sm" variant="ghost" onClick={() => void saveTitle()} disabled={updateList.isPending || !editedTitle.trim()} aria-label="Save name">
                  <Check aria-hidden />
                </Button>
                <Button size="icon-sm" variant="ghost" onClick={cancelEditTitle} disabled={updateList.isPending} aria-label="Cancel">
                  <X aria-hidden />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="font-display text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-brand">
                  {list.listName}
                </h1>
                {isOwner && (
                  <Button size="icon-sm" variant="ghost" onClick={startEditTitle} aria-label="Rename list">
                    <Pencil aria-hidden />
                  </Button>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              {isOwner ? (
                <ListTypeSelector listId={list.id} currentType={list.listType} variant="badge" />
              ) : (
                <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
              )}
            </div>
          </div>
          <StatStrip stats={stats} />
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <SearchField id="list-restaurant-search" value={globalFilter} onChange={setGlobalFilter} placeholder="Search this list" />
        <div className="ml-auto flex items-center gap-2">
          <ViewToggle value={viewMode} onChange={(m) => { setViewMode(m); setSorting([]); setSelectedRestaurantId(null); setMapCenter(null); }} />
          {(isOwner || list.listType === "featured") && <ShareListButton listId={list.id} />}
          {isOwner && (
            <Button variant="brand" onClick={() => setIsAddOpen(true)}>
              <Plus aria-hidden /> Add restaurants
            </Button>
          )}
        </div>
      </div>

      {viewMode === "map" ? (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <div className="sticky top-[72px] h-[calc(100vh-14rem)] overflow-hidden rounded-sm border border-foreground">
              <RestaurantMap
                height="100%"
                center={mapCenter || defaultCenter}
                savedRestaurants={mapRestaurants}
                onSavedRestaurantClick={handleMapPinClick}
                onGoogleApiLoaded={handleGoogleApiLoaded}
                hoveredId={hoveredRestaurantId}
                selectedId={selectedRestaurantId}
                zoom={mapCenter || restaurants.length === 1 ? 15 : 12}
              />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <DataTable
              table={table}
              emptyMessage="This list is empty."
              selectedRowId={selectedRestaurantId}
              hoveredRowId={hoveredRestaurantId}
              onRowClick={handleRowClick}
              expandedRowId={selectedRestaurantId}
              renderExpanded={(r) => <RestaurantExpandedRow restaurant={r} listId={listId} />}
              onRowHoverChange={(r) => setHoveredRestaurantId(r?.id ?? null)}
              maxHeightClassName="max-h-[calc(100vh-14rem)]"
              footer={<span>{restaurants.length} {restaurants.length === 1 ? "place" : "places"}</span>}
            />
          </div>
        </div>
      ) : (
        <DataTable
          table={table}
          emptyMessage={isOwner ? "This list is empty." : "This list is empty."}
          onRowClick={handleRowClick}
          expandedRowId={selectedRestaurantId}
          renderExpanded={(r) => <RestaurantExpandedRow restaurant={r} listId={listId} />}
          emptyAction={
            isOwner ? (
              <button type="button" className="text-brand underline underline-offset-[3px]" onClick={() => setIsAddOpen(true)}>
                Add restaurants
              </button>
            ) : undefined
          }
          footer={<span>{table.getFilteredRowModel().rows.length} of {restaurants.length} {restaurants.length === 1 ? "place" : "places"}</span>}
        />
      )}

      {/* Loads the Google Maps API so the add modal's autocomplete works in table mode. */}
      {isOwner && viewMode !== "map" && <GoogleMapsLoader onLoaded={handleGoogleApiLoaded} />}

      {isOwner && (
        <AddRestaurantModal listId={listId} isOpen={isAddOpen} onOpenChange={setIsAddOpen} isGoogleApiLoaded={isGoogleApiLoaded} />
      )}
    </div>
  );
};

export default ListDetailsPage;
